import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import UploadLogo from "../../../components/UploadLogo";
import * as Api from "../../../api";
import { menuLayoutSchema, defaultMenuLayoutValues } from "../schemas/menuLayoutSchema";
import { THEME_COLOR3 } from "../../../constants";

const MenuLayoutDialog = ({ open, onClose, editData, onSubmit, isSubmitting }) => {
  const [imageLoading, setImageLoading] = useState(false);
  const [logoImg, setLogoImg] = useState("");
  const isEditMode = !!editData;

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(menuLayoutSchema),
    defaultValues: defaultMenuLayoutValues,
  });

  useEffect(() => {
    if (open) {
      if (editData) {
        // Edit mode - populate form
        reset({
          name: editData.name || "",
          description: editData.description || "",
          image: editData.image || "",
        });
        if (editData.image) {
          setLogoImg({ image: editData.image });
        }
      } else {
        // Create mode - clear form
        reset(defaultMenuLayoutValues);
        setLogoImg("");
      }
    }
  }, [open, editData, reset]);

  const uploadLogoFile = (data, file) => {
    setImageLoading(true);
    
    const formData = new FormData();
    formData.append("image", file);

    Api.uploadImageFile(formData)
      .then((response) => {
        if (response.success) {
          const imageUrl = response.data.location || response.data.url;
          setValue("image", imageUrl, { shouldValidate: true });
          // Use the uploaded URL, not the local blob URL
          setLogoImg({ image: imageUrl });
        } else {
          console.error("Image upload failed:", response.msg);
          setLogoImg("");
          setValue("image", "", { shouldValidate: true });
        }
        setImageLoading(false);
      })
      .catch((error) => {
        console.error("Image upload error:", error);
        setLogoImg("");
        setValue("image", "", { shouldValidate: true });
        setImageLoading(false);
      });
  };

  const onRemoveLogo = () => {
    setLogoImg("");
    setValue("image", "", { shouldValidate: true });
  };

  const handleFormSubmit = (data) => {
    const payload = {
      name: data.name,
      description: data.description || "",
      image: data.image,
    };

    if (isEditMode) {
      onSubmit({ id: editData._id, data: payload });
    } else {
      onSubmit(payload);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      reset(defaultMenuLayoutValues);
      setLogoImg("");
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={styles.dialogTitle}>
        {isEditMode ? "Edit Menu Layout" : "Create Menu Layout"}
      </DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          <Box sx={styles.formContainer}>
            {/* Name Field */}
            <Box sx={styles.fieldContainer}>
              <Typography sx={styles.label}>
                Name <span style={{ color: "red" }}>*</span>
              </Typography>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    placeholder="Enter layout name"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    sx={styles.textField}
                  />
                )}
              />
            </Box>

            {/* Description Field */}
            <Box sx={styles.fieldContainer}>
              <Typography sx={styles.label}>Description</Typography>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Enter layout description (optional)"
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    sx={styles.textField}
                  />
                )}
              />
            </Box>

            {/* Image Upload Field */}
            <Box sx={styles.fieldContainer}>
              <Typography sx={styles.label}>
                Image <span style={{ color: "red" }}>*</span>
              </Typography>
              <Controller
                name="image"
                control={control}
                render={({ field }) => (
                  <Box>
                    <UploadLogo
                      logoImg={logoImg}
                      onRemove={onRemoveLogo}
                      uploadFile={uploadLogoFile}
                      imageLoading={imageLoading}
                      type="menuLayout"
                      showViewUploadIcon={true}
                    />
                    {errors.image && (
                      <Typography sx={styles.errorText}>
                        {errors.image.message}
                      </Typography>
                    )}
                  </Box>
                )}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={styles.dialogActions}>
          <Button
            onClick={handleClose}
            disabled={isSubmitting}
            sx={styles.cancelButton}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || imageLoading}
            sx={styles.submitButton}
          >
            {isSubmitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : isEditMode ? (
              "Update"
            ) : (
              "Create"
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default MenuLayoutDialog;

const styles = {
  dialogTitle: {
    fontFamily: "GilroyBold",
    fontSize: 18,
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  fieldContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
  },
  label: {
    fontFamily: "GilroySemiBold",
    fontSize: 14,
    color: "#333",
  },
  textField: {
    "& .MuiInputBase-root": {
      fontFamily: "GilroyRegular",
      fontSize: 14,
    },
  },
  errorText: {
    color: "error.main",
    fontSize: 12,
    fontFamily: "GilroyRegular",
    marginTop: 0.5,
  },
  dialogActions: {
    padding: 2,
    gap: 1,
  },
  cancelButton: {
    fontFamily: "GilroySemiBold",
    color: "#666",
    borderColor: THEME_COLOR3,
    border: `1px solid ${THEME_COLOR3}`,
    "&:hover": {
      borderColor: "#000",
      backgroundColor: "rgba(0,0,0,0.05)",
    },
  },
  submitButton: {
    fontFamily: "GilroySemiBold",
    backgroundColor: "#000",
    color: "#FFF",
    minWidth: 100,
    "&:hover": {
      backgroundColor: "#333",
    },
    "&:disabled": {
      backgroundColor: "#ccc",
      color: "#666",
    },
  },
};
